import { useState, useEffect } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { toast } from 'sonner';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { RadioGroup, RadioGroupItem } from '@/components/ui/radio-group';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '@/components/ui/tooltip';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import {
  Play,
  FileText,
  CheckCircle2,
  Clock,
  Download,
  Eye,
  Save,
  X,
  MoreVertical,
  Plus,
  Trash2,
  RefreshCw,
  Calendar,
  ChevronRight,
  Search,
  HelpCircle,
  TrendingUp,
} from 'lucide-react';
import { AssetSelectionDialog } from '@/components/depreciation/asset-selection-dialog';
import { RevaluationBatchGrid } from '@/components/revaluation/revaluation-batch-grid';
import { ConfirmationModal } from '@/components/ui/confirmation-modal';
import { ComingSoonModal } from '@/components/coming-soon-modal';
import { DemoModeWarningModal } from '@/components/ui/demo-mode-warning-modal';
import type { Asset } from '@/types/asset';
import type { RevaluationBatch, RevaluationItem, RevaluationMode } from '@/types/revaluation';
import type { mockAssets } from '@/lib/mock-data';
import { dataService } from '@/services/data-service';
import { useMode } from '@/providers/ModeProvider';
import { 
  calculateRevaluedNBV, 
  validateRevaluationAmount, 
  getRevaluationAmountPlaceholder,
  getRevaluationModeHelp,
  recalculateRevaluationItems 
} from '@/lib/revaluation-calculator';
import { mockRevaluationBatches } from '@/lib/mock-revaluation-data';

export default function RevaluationPage(): React.ReactElement {
  const location = useLocation();
  const navigate = useNavigate();
  const { isDemo, setMode } = useMode();
  const [showAssetSelection, setShowAssetSelection] = useState(false);
  const [currentBatch, setCurrentBatch] = useState<RevaluationBatch | null>(null);
  const [selectedItemIds, setSelectedItemIds] = useState<Set<string>>(new Set());
  const [showCancelConfirm, setShowCancelConfirm] = useState(false);
  const [showProcessConfirm, setShowProcessConfirm] = useState(false);
  const [showRemoveConfirm, setShowRemoveConfirm] = useState(false);
  const [lastSavedAt, setLastSavedAt] = useState<Date | null>(null);
  const [showModifiedHighlight, setShowModifiedHighlight] = useState(false);
  const [completedBatches, setCompletedBatches] = useState<any[]>(mockRevaluationBatches);
  const [isLoadingBatches, setIsLoadingBatches] = useState(false);
  const [hasUnsavedChanges, setHasUnsavedChanges] = useState(false);
  const [originalBatch, setOriginalBatch] = useState<RevaluationBatch | null>(null);
  const [showComingSoon, setShowComingSoon] = useState(false);
  const [showDemoWarning, setShowDemoWarning] = useState(false);
  const [statistics, setStatistics] = useState<any>(null);
  const [isLoadingStatistics, setIsLoadingStatistics] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');

  useEffect(() => {
    const fetchData = async () => {
      setIsLoadingBatches(true);
      setIsLoadingStatistics(true);
      try {
        // Fetch batches and statistics in parallel
        const [batches, stats] = await Promise.all([
          dataService.getRevaluationBatches(),
          dataService.getRevaluationStatistics()
        ]);
        
        if (batches && batches.length > 0) {
          const sortedBatches = batches.sort((a: any, b: any) => 
            new Date(b.dateOfRun).getTime() - new Date(a.dateOfRun).getTime()
          );
          setCompletedBatches(sortedBatches);
        } else {
          setCompletedBatches(mockRevaluationBatches);
        }
        
        setStatistics(stats);
      } catch (error) {
        console.error('Error fetching revaluation data:', error);
        setCompletedBatches(mockRevaluationBatches);
        // Set default statistics on error
        setStatistics({
          year: new Date().getFullYear(),
          totalRevaluationAdjustment: 125400.00,
          totalAssetsRevalued: 23,
          totalBatches: 4,
          pendingBatchCount: 1,
          pendingAdjustment: 15200.00
        });
      } finally {
        setIsLoadingBatches(false);
        setIsLoadingStatistics(false);
      }
    };

    if (!currentBatch) {
      fetchData();
    }
  }, [currentBatch]);

  // Handle selected assets passed from assets page
  useEffect(() => {
    const state = location.state as { selectedAssets?: Asset[] } | null;
    if (state?.selectedAssets && state.selectedAssets.length > 0) {
      handleAssetsSelected(state.selectedAssets);
      // Clear the state to prevent re-triggering on navigation
      window.history.replaceState({}, document.title);
    }
  }, [location.state]);

  // Auto-recalculate items when header revaluation mode or amount changes
  useEffect(() => {
    if (!currentBatch || currentBatch.items.length === 0) return;
    
    // Recalculate only non-overridden items
    const updatedItems = currentBatch.items.map(item => {
      // Skip overridden items
      if (item.isOverridden) {
        return item;
      }

      const revaluedNBV = calculateRevaluedNBV(
        item.currentNBV,
        currentBatch.revaluationMode,
        currentBatch.revaluationAmount
      );
      const revaluationAdjustment = revaluedNBV - item.currentNBV;

      return {
        ...item,
        revaluedNBV,
        revaluationAdjustment,
        aiRevaluedNBV: revaluedNBV,
      };
    });

    // Only update if there are actual changes
    const hasChanges = updatedItems.some((item, index) => 
      item.revaluedNBV !== currentBatch.items[index].revaluedNBV ||
      item.revaluationAdjustment !== currentBatch.items[index].revaluationAdjustment
    );

    if (hasChanges) {
      const totalRevaluationAdjustment = updatedItems.reduce((sum, item) => sum + item.revaluationAdjustment, 0);
      const totalCurrentNBV = updatedItems.reduce((sum, item) => sum + item.currentNBV, 0);
      const totalRevaluedNBV = updatedItems.reduce((sum, item) => sum + item.revaluedNBV, 0);

      setCurrentBatch({
        ...currentBatch,
        items: updatedItems,
        totalRevaluationAdjustment,
        totalCurrentNBV,
        totalRevaluedNBV,
      });
    }
  }, [currentBatch?.revaluationMode, currentBatch?.revaluationAmount]);

  const handleStartRun = () => {
    if (isDemo) {
      setShowDemoWarning(true);
      return;
    }
    setShowAssetSelection(true);
  };

  const handleAddMoreAssets = () => {
    setShowAssetSelection(true);
  };

  const handleRemoveSelectedItems = () => {
    if (!currentBatch || selectedItemIds.size === 0) return;
    setShowRemoveConfirm(true);
  };

  const confirmRemoveSelectedItems = () => {
    if (!currentBatch || selectedItemIds.size === 0) return;
    
    const updatedItems = currentBatch.items.filter(item => !selectedItemIds.has(item.id));
    
    const totalRevaluationAdjustment = updatedItems.reduce((sum, item) => sum + item.revaluationAdjustment, 0);
    const totalCurrentNBV = updatedItems.reduce((sum, item) => sum + item.currentNBV, 0);
    const totalRevaluedNBV = updatedItems.reduce((sum, item) => sum + item.revaluedNBV, 0);
    
    setCurrentBatch({
      ...currentBatch,
      items: updatedItems,
      totalRevaluationAdjustment,
      totalCurrentNBV,
      totalRevaluedNBV,
    });
    
    setSelectedItemIds(new Set());
    setShowRemoveConfirm(false);
  };

  const handleAssetsSelected = (selectedAssets: Asset[]) => {
    // Create revaluation items from selected assets - default to 0.00 until user enters revaluation amount
    const newItems: RevaluationItem[] = selectedAssets.map((asset, index) => {
      return {
        id: `rev-item-${Date.now()}-${index}`,
        assetId: asset.id,
        assetCode: asset.assetId,
        assetDescription: asset.name,
        category: asset.category,
        location: asset.location,
        currentNBV: asset.netBookValue,
        aiRevaluedNBV: 0, // Default to 0.00 until user enters revaluation amount
        confidence: 'High' as const,
        revaluedNBV: 0, // Default to 0.00 (no adjustment)
        revaluationAdjustment: 0, // Default to 0 until user enters revaluation amount
        validationStatus: 'valid',
      };
    });

    // If we have an existing batch, add to it; otherwise create new batch
    if (currentBatch) {
      // Filter out duplicates based on assetId
      const existingAssetIds = new Set(currentBatch.items.map(item => item.assetId));
      const uniqueNewItems = newItems.filter(item => !existingAssetIds.has(item.assetId));
      
      const allItems = [...currentBatch.items, ...uniqueNewItems];
      const totalRevaluationAdjustment = allItems.reduce((sum, item) => sum + item.revaluationAdjustment, 0);
      const totalCurrentNBV = allItems.reduce((sum, item) => sum + item.currentNBV, 0);
      const totalRevaluedNBV = allItems.reduce((sum, item) => sum + item.revaluedNBV, 0);
      
      setCurrentBatch({
        ...currentBatch,
        items: allItems,
        totalRevaluationAdjustment,
        totalCurrentNBV,
        totalRevaluedNBV,
      });
    } else {
      // Create new batch
      const totalRevaluationAdjustment = newItems.reduce((sum, item) => sum + item.revaluationAdjustment, 0);
      const totalCurrentNBV = newItems.reduce((sum, item) => sum + item.currentNBV, 0);
      const totalRevaluedNBV = newItems.reduce((sum, item) => sum + item.revaluedNBV, 0);

      const batch: RevaluationBatch = {
        id: `batch-${Date.now()}`,
        batchReference: '',
        dateOfRun: new Date().toISOString().split('T')[0],
        status: 'In Progress',
        notes: '',
        items: newItems,
        revaluationMode: 'ByPercentage',
        revaluationAmount: 0,
        createdBy: 'Current User',
        createdDate: new Date().toISOString(),
        modifiedBy: 'Current User',
        modifiedDate: new Date().toISOString(),
        preparedBy: 'Current User',
        preparedDate: new Date().toISOString(),
        currentStep: 'Prepare',
        totalRevaluationAdjustment,
        totalCurrentNBV,
        totalRevaluedNBV,
        currency: 'GBP',
      };

      setCurrentBatch(batch);
    }
    
    setShowAssetSelection(false);
  };

  const handleUpdateItem = (itemId: string, updates: Partial<RevaluationItem>) => {
    if (!currentBatch) return;

    const updatedItems = currentBatch.items.map(item =>
      item.id === itemId ? { ...item, ...updates } : item
    );

    const totalRevaluationAdjustment = updatedItems.reduce((sum, item) => sum + item.revaluationAdjustment, 0);
    const totalCurrentNBV = updatedItems.reduce((sum, item) => sum + item.currentNBV, 0);
    const totalRevaluedNBV = updatedItems.reduce((sum, item) => sum + item.revaluedNBV, 0);

    setCurrentBatch({
      ...currentBatch,
      items: updatedItems,
      totalRevaluationAdjustment,
      totalCurrentNBV,
      totalRevaluedNBV,
    });
  };

  const handleReapplyDefaults = (applyToAll: boolean) => {
    if (!currentBatch) return;

    const updatedItems = recalculateRevaluationItems(
      currentBatch.items,
      currentBatch.revaluationMode,
      currentBatch.revaluationAmount,
      applyToAll
    );

    const totalRevaluationAdjustment = updatedItems.reduce((sum, item) => sum + item.revaluationAdjustment, 0);
    const totalCurrentNBV = updatedItems.reduce((sum, item) => sum + item.currentNBV, 0);
    const totalRevaluedNBV = updatedItems.reduce((sum, item) => sum + item.revaluedNBV, 0);

    setCurrentBatch({
      ...currentBatch,
      items: updatedItems,
      totalRevaluationAdjustment,
      totalCurrentNBV,
      totalRevaluedNBV,
    });

    toast.success('Defaults reapplied', {
      description: applyToAll 
        ? 'All lines have been recalculated from header values.'
        : 'Unmodified lines have been recalculated from header values.',
    });
  };
  const isServerGeneratedId = (id: string) => {
    const guidRegex = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;
    return guidRegex.test(id);
  };


  const handleSaveBatch = async () => {
    if (!currentBatch) return;
    
    try {
      const batchToSave = {
        ...currentBatch,
        status: currentBatch.status || 'In Progress',
      };
      
      if (currentBatch.id && isServerGeneratedId(currentBatch.id)) {
        const updatedBatch = await dataService.updateRevaluationBatch(currentBatch.id, batchToSave);
        setCurrentBatch({
          ...updatedBatch,
          dateOfRun: updatedBatch.dateOfRun?.split('T')[0] || updatedBatch.dateOfRun,
        });
      } else {
        const savedBatch = await dataService.createRevaluationBatch(batchToSave);
        setCurrentBatch({
          ...savedBatch,
          dateOfRun: savedBatch.dateOfRun?.split('T')[0] || savedBatch.dateOfRun,
        });
      }
      
      setShowModifiedHighlight(true);
      setTimeout(() => setShowModifiedHighlight(false), 2000);
      
      toast.success('Batch saved', { description: 'Revaluation batch saved successfully' });
    } catch (error) {
      console.error('Failed to save revaluation batch:', error);
      toast.error('Failed to save batch', { description: 'Please ensure you are in API mode and try again.' });
    }
  };

  const handleCancelBatch = () => {
    setShowCancelConfirm(true);
  };

  const confirmCancelBatch = () => {
    setCurrentBatch(null);
    setSelectedItemIds(new Set());
    setLastSavedAt(null);
    setShowCancelConfirm(false);
  };

  const handleProcessBatch = () => {
    if (!currentBatch) return;

    // Validate mandatory fields
    if (!currentBatch.batchReference || currentBatch.batchReference.trim() === '') {
      toast.error('Reference is required', {
        description: 'Please enter a reference before processing.',
      });
      return;
    }

    if (!currentBatch.dateOfRun || currentBatch.dateOfRun.trim() === '') {
      toast.error('Date is required', {
        description: 'Please select a date before processing.',
      });
      return;
    }

    setShowProcessConfirm(true);
  };

  const confirmProcessBatch = async () => {
    if (!currentBatch) return;
    
    const reference = currentBatch.batchReference;
    
    try {
      // Create a transaction for each item in the batch
      for (const item of currentBatch.items) {
        await dataService.createTransaction({
          transactionDate: currentBatch.dateOfRun,
          type: 'revaluation',
          assetId: item.assetId,
          amount: item.revaluationAdjustment,
          currency: currentBatch.currency,
          status: 'posted',
          reference: currentBatch.batchReference,
          description: `Revaluation adjustment - ${currentBatch.batchReference || 'Batch'}`,
          preparedBy: currentBatch.preparedBy,
          preparedDate: currentBatch.preparedDate,
          postedBy: 'Current User',
          postedDate: new Date().toISOString(),
        });
      }
      
      // Save the batch to the database with completed status
      const completedBatch = {
        ...currentBatch,
        status: 'Completed',
        postedBy: 'Current User',
        postedDate: new Date().toISOString(),
      };
      
      // Create or update the batch in the database
      if (currentBatch.id && isServerGeneratedId(currentBatch.id)) {
        await dataService.updateRevaluationBatch(currentBatch.id, completedBatch);
      } else {
        await dataService.createRevaluationBatch(completedBatch);
      }
      
      // Show success toast
      toast.success('Revaluation Created Successfully', {
        description: `Reference: ${reference}`,
      });
      
      // Clear batch and navigate back to processing screen
      setCurrentBatch(null);
      setSelectedItemIds(new Set());
      setLastSavedAt(null);
      setShowProcessConfirm(false);
      
      // Navigate back to processing screen
      navigate('/transactions/revaluation');
    } catch (error) {
      console.error('Failed to process revaluation batch:', error);
      toast.error('Failed to process revaluation batch', {
        description: 'Please ensure you are in API mode and try again.',
      });
      setShowProcessConfirm(false);
    }
  };

  const handleViewLastBatch = () => {
    // In a real app, this would load the last batch
    console.log('View last batch');
  };

  // Landing page view (no active batch)
  if (!currentBatch) {
    return (
      <div className="flex h-full flex-col">
        {/* Page Header */}
        <div className="border-b bg-card p-6">
          <div className="flex items-center justify-between">
            <div>
              <h1 className="text-2xl font-bold tracking-tight flex items-center gap-2">
                <TrendingUp className="h-6 w-6 text-primary" />
                Asset Revaluation
              </h1>
              <p className="text-sm text-muted-foreground mt-1">Revaluation and fair value adjustments</p>
            </div>
          <div className="flex gap-2">
            <Button variant="outline" onClick={() => setShowComingSoon(true)}>
              <Eye className="mr-2 h-4 w-4" />
              View Last Batch Summary
            </Button>
            <Button onClick={handleStartRun}>
              <Play className="mr-2 h-4 w-4" />
              Start New Revaluation Batch
            </Button>
          </div>
          </div>
        </div>

        {/* Content */}
        <div className="flex-1 overflow-auto">
          <div className="space-y-6 p-6">
        <div className="grid gap-4 md:grid-cols-3">
          {isLoadingStatistics ? (
            <>
              <Card className="border-l-4 border-l-blue-500">
                <CardContent className="p-6">
                  <div className="animate-pulse space-y-4">
                    <div className="h-4 bg-muted rounded w-1/2"></div>
                    <div className="h-8 bg-muted rounded w-3/4"></div>
                  </div>
                </CardContent>
              </Card>
              <Card className="border-l-4 border-l-green-500">
                <CardContent className="p-6">
                  <div className="animate-pulse space-y-4">
                    <div className="h-4 bg-muted rounded w-1/2"></div>
                    <div className="h-8 bg-muted rounded w-3/4"></div>
                  </div>
                </CardContent>
              </Card>
              <Card className="border-l-4 border-l-yellow-500">
                <CardContent className="p-6">
                  <div className="animate-pulse space-y-4">
                    <div className="h-4 bg-muted rounded w-1/2"></div>
                    <div className="h-8 bg-muted rounded w-3/4"></div>
                  </div>
                </CardContent>
              </Card>
            </>
          ) : (
            <>
              <Card className="relative overflow-hidden border-l-4 border-l-[oklch(0.5_0.09_194.77)] transition-all duration-200 cursor-pointer hover:shadow-lg hover:scale-[1.02]">
                <div className="absolute top-0 right-0 w-32 h-32 bg-[oklch(0.5_0.09_194.77)]/5 rounded-full -mr-16 -mt-16" />
                <CardContent className="relative p-6">
                  <div className="flex items-center gap-2 mb-3">
                    <div className="p-2 rounded-full bg-[oklch(0.5_0.09_194.77)]/10">
                      <TrendingUp className="h-4 w-4 text-[oklch(0.5_0.09_194.77)]" />
                    </div>
                    <p className="text-sm font-medium text-muted-foreground">Net Revaluation Adjustments</p>
                  </div>
                  <h3 className="text-3xl font-bold text-[oklch(0.5_0.09_194.77)] mb-2">
                    {(statistics?.totalRevaluationAdjustment || 0) >= 0 ? '+' : ''}£{(statistics?.totalRevaluationAdjustment || 0).toLocaleString('en-GB', { minimumFractionDigits: 2 })}
                  </h3>
                  <p className="text-sm text-muted-foreground">
                    This year ({statistics?.year || new Date().getFullYear()})
                  </p>
                </CardContent>
              </Card>

              <Card className="relative overflow-hidden border-l-4 border-l-green-500 transition-all duration-200 cursor-pointer hover:shadow-lg hover:scale-[1.02]">
                <div className="absolute top-0 right-0 w-32 h-32 bg-green-500/5 rounded-full -mr-16 -mt-16" />
                <CardContent className="relative p-6">
                  <div className="flex items-center gap-2 mb-3">
                    <div className="p-2 rounded-full bg-green-50 dark:bg-green-950">
                      <RefreshCw className="h-4 w-4 text-green-600 dark:text-green-400" />
                    </div>
                    <p className="text-sm font-medium text-muted-foreground">Assets Revalued</p>
                  </div>
                  <h3 className="text-3xl font-bold text-green-600 dark:text-green-400 mb-2">{statistics?.totalAssetsRevalued || 0}</h3>
                  <p className="text-sm text-muted-foreground">
                    Across {statistics?.totalBatches || 0} revaluation {(statistics?.totalBatches || 0) === 1 ? 'batch' : 'batches'}
                  </p>
                </CardContent>
              </Card>

              <Card className="relative overflow-hidden border-l-4 border-l-yellow-500 transition-all duration-200 cursor-pointer hover:shadow-lg hover:scale-[1.02]">
                <div className="absolute top-0 right-0 w-32 h-32 bg-yellow-500/5 rounded-full -mr-16 -mt-16" />
                <CardContent className="relative p-6">
                  <div className="flex items-center gap-2 mb-3">
                    <div className="p-2 rounded-full bg-yellow-50 dark:bg-yellow-950">
                      <Clock className="h-4 w-4 text-yellow-600 dark:text-yellow-400" />
                    </div>
                    <p className="text-sm font-medium text-muted-foreground">Pending Approval</p>
                  </div>
                  <h3 className="text-3xl font-bold text-yellow-600 dark:text-yellow-400 mb-2">{statistics?.pendingBatchCount || 0}</h3>
                  <p className="text-sm text-muted-foreground">
                    {(statistics?.pendingAdjustment || 0) >= 0 ? '+' : ''}£{(statistics?.pendingAdjustment || 0).toLocaleString('en-GB', { minimumFractionDigits: 2 })} adjustment
                  </p>
                </CardContent>
              </Card>
            </>
          )}
        </div>

        {/* Recent Batches */}
        <div>
          <div className="flex items-center justify-between mb-6">
            <div>
              <h2 className="text-xl font-semibold">Transaction History</h2>
              <p className="text-sm text-muted-foreground mt-1">View and manage all revaluation transactions</p>
            </div>
            <div className="relative w-80">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-muted-foreground" />
              <Input
                type="text"
                placeholder="Search transactions..."
                value={searchQuery}
                onChange={(e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => setSearchQuery(e.target.value)}
                className="pl-9 bg-background"
              />
            </div>
          </div>
          
          <div className="space-y-3">
            {isLoadingBatches ? (
              <div className="text-center py-8 text-muted-foreground">Loading batches...</div>
            ) : completedBatches.filter(batch => {
              const query = searchQuery.toLowerCase();
              return (
                (batch.batchReference || batch.id).toLowerCase().includes(query) ||
                batch.status.toLowerCase().includes(query) ||
                (batch.postedBy && batch.postedBy.toLowerCase().includes(query))
              );
            }).length === 0 ? (
              <div className="text-center py-8 text-muted-foreground">No transactions found</div>
            ) : (
              completedBatches.filter(batch => {
                const query = searchQuery.toLowerCase();
                return (
                  (batch.batchReference || batch.id).toLowerCase().includes(query) ||
                  batch.status.toLowerCase().includes(query) ||
                  (batch.postedBy && batch.postedBy.toLowerCase().includes(query))
                );
              }).map((batch) => (
                <div key={batch.id} className="flex items-center justify-between rounded-lg border border-border p-4 bg-background hover:bg-muted/50 transition-colors shadow-sm">
                  <div className="space-y-1">
                    <div className="font-semibold">{batch.batchReference || batch.id}</div>
                    <div className="text-sm text-muted-foreground">
                      {batch.dateOfRun ? new Date(batch.dateOfRun).toLocaleDateString('en-GB') : batch.date} • {batch.items?.length || batch.assets} assets • {(batch.totalRevaluationAdjustment || batch.amount) >= 0 ? '+' : ''}£{(batch.totalRevaluationAdjustment || batch.amount).toLocaleString('en-GB', { minimumFractionDigits: 2 })}
                    </div>
                    {batch.postedBy && batch.postedDate && (
                      <div className="text-xs text-muted-foreground">
                        Posted by {batch.postedBy} on {new Date(batch.postedDate).toLocaleString('en-GB')}
                      </div>
                    )}
                  </div>
                  <div className="flex items-center gap-2">
                    <Badge 
                      variant="secondary"
                      className={
                        batch.status === 'Completed'
                          ? 'bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-400'
                          : 'bg-blue-100 text-blue-700 dark:bg-blue-900/30 dark:text-blue-400'
                      }
                    >
                      <CheckCircle2 className="h-3 w-3 mr-1" />
                      {batch.status}
                    </Badge>
                    <Button 
                      variant="outline" 
                      size="sm"
                      onClick={() => {
                        // Format the date to YYYY-MM-DD for the date input
                        const formattedBatch = {
                          ...batch,
                          dateOfRun: batch.dateOfRun.split('T')[0]
                        };
                        setCurrentBatch(formattedBatch);
                      }}
                    >
                      <Eye className="h-4 w-4 mr-2" />
                      View
                    </Button>
                  </div>
                </div>
              ))
            )}
          </div>
        </div>

          </div>
        </div>

        <AssetSelectionDialog
          open={showAssetSelection}
          onOpenChange={setShowAssetSelection}
          onConfirm={handleAssetsSelected}
          title="Select Assets for Revaluation"
          description="Choose one or more assets to add to this revaluation batch."
          excludeAssetIds={[]}
        />

        <ComingSoonModal
          open={showComingSoon}
          onOpenChange={setShowComingSoon}
          title="Batch Summary Coming Soon"
          description="The ability to view the last batch summary will be available in an upcoming release."
        />
      </div>
    );
  }

  // Batch processing view
  const readonly = currentBatch.status === 'Completed';

  return (
    <div className="relative flex flex-col h-full">
      <div className="flex-1 overflow-auto pb-32">
        {/* Page Header */}
        <div className="bg-card p-6 pb-0">
          <div className="flex flex-col gap-4 lg:flex-row lg:items-center lg:justify-between mb-6">
            <div>
              <h1 className="text-2xl font-bold tracking-tight flex items-center gap-2">
                <RefreshCw className="h-6 w-6 text-primary" />
                Asset Revaluation
              </h1>
              <p className="text-sm text-muted-foreground mt-1">
                Last Modified{' '}
                <span 
                  className={`font-semibold transition-colors duration-500 ${
                    showModifiedHighlight ? 'text-green-600 dark:text-green-400' : 'text-foreground'
                  }`}
                >
                  {currentBatch.modifiedDate ? new Date(currentBatch.modifiedDate).toLocaleString('en-GB', { 
                    weekday: 'long',
                    day: 'numeric',
                    month: 'long',
                    year: 'numeric',
                    hour: '2-digit',
                    minute: '2-digit'
                  }).replace(/(\d+)/, (match) => {
                    const day = parseInt(match);
                    const suffix = day === 1 || day === 21 || day === 31 ? 'st' : 
                                   day === 2 || day === 22 ? 'nd' : 
                                   day === 3 || day === 23 ? 'rd' : 'th';
                    return `${day}${suffix}`;
                  }) : 'Never'}
                </span>
              </p>
            </div>
            <Badge
              variant="secondary"
              className={`text-base px-3 py-1 ${
                currentBatch.status === 'In Progress'
                  ? 'bg-blue-100 text-blue-700 dark:bg-blue-900/30 dark:text-blue-400'
                  : 'bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-400'
              }`}
            >
              {currentBatch.status === 'Completed' && <CheckCircle2 className="h-3 w-3 mr-1" />}
              {currentBatch.status === 'In Progress' && <Clock className="h-3 w-3 mr-1" />}
              {currentBatch.status}
            </Badge>
          </div>
          
          <div className="-mx-6 px-6 pt-6 pb-4 border-y border-border bg-[#F9FAFB] dark:bg-[#121212]">
            <div className="flex gap-4">
              {/* Left Column: Reference and Revalue */}
              <div className="space-y-2 flex-shrink-0" style={{ width: '300px' }}>
                <div className="space-y-2">
                  <Label htmlFor="batch-ref">
                    Reference <span className="text-red-500">*</span>
                  </Label>
                  <Input 
                    id="batch-ref" 
                    value={currentBatch.batchReference} 
                    onChange={(e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => setCurrentBatch({ ...currentBatch, batchReference: e.target.value })}
                    readOnly={readonly}
                    tabIndex={1}
                  />
                </div>

                <div className="space-y-2">
                  <div className="flex items-center gap-2">
                    <Label htmlFor="revalue-mode">
                      Revalue <span className="text-red-500">*</span>
                    </Label>
                    <TooltipProvider>
                      <Tooltip>
                        <TooltipTrigger asChild>
                          <HelpCircle className="h-4 w-4 text-muted-foreground cursor-help" />
                        </TooltipTrigger>
                        <TooltipContent side="right" className="max-w-xs">
                          <div className="space-y-2 text-sm">
                            <p><strong>By Percentage</strong> — apply an uplift/downlift by a percentage of current NBV</p>
                            <p><strong>By Value</strong> — add/subtract a fixed amount to current NBV</p>
                            <p><strong>To Value</strong> — set NBV to an exact target value</p>
                          </div>
                        </TooltipContent>
                      </Tooltip>
                    </TooltipProvider>
                  </div>
                  <Select
                    value={currentBatch.revaluationMode}
                    onValueChange={(value: RevaluationMode) => {
                      if (readonly) return;
                      
                      // Check if there are any overridden items
                      const hasOverrides = currentBatch.items.some(item => item.isOverridden);
                      
                      if (hasOverrides && currentBatch.items.length > 0) {
                        toast.info('Defaults recalculated from header', {
                          description: "Use 'Reapply Defaults' to update overridden lines.",
                        });
                      }
                      
                      setCurrentBatch({ ...currentBatch, revaluationMode: value });
                    }}
                    disabled={readonly}
                  >
                    <SelectTrigger id="revalue-mode" className="bg-white dark:bg-black" tabIndex={3}>
                      <SelectValue placeholder="Select revaluation mode" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="ByPercentage">By Percentage</SelectItem>
                      <SelectItem value="ByValue">By Value</SelectItem>
                      <SelectItem value="ToValue">To Value</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              </div>

              {/* Middle Column: Date and Revaluation Amount */}
              <div className="space-y-2 flex-shrink-0" style={{ width: '300px' }}>
                <div className="space-y-2">
                  <Label htmlFor="date-of-run">
                    Date <span className="text-red-500">*</span>
                  </Label>
                  <Input 
                    id="date-of-run" 
                    type="date" 
                    value={currentBatch.dateOfRun} 
                    onChange={(e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => setCurrentBatch({ ...currentBatch, dateOfRun: e.target.value })}
                    readOnly={readonly}
                    tabIndex={2}
                  />
                </div>

                <div className="space-y-2">
                  <Label htmlFor="revaluation-amount">
                    Revaluation Amount <span className="text-red-500">*</span>
                  </Label>
                  <Input 
                    id="revaluation-amount" 
                    type="number"
                    step={currentBatch.revaluationMode === 'ByPercentage' ? '0.0001' : '0.01'}
                    value={currentBatch.revaluationAmount}
                    onChange={(e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
                      if (readonly) return;
                      
                      // Allow empty string or parse the value (including negatives)
                      const value = e.target.value === '' ? 0 : parseFloat(e.target.value);
                      
                      // Only validate if we have a valid number
                      if (!isNaN(value)) {
                        // Validate the amount
                        const validation = validateRevaluationAmount(value, currentBatch.revaluationMode);
                        if (!validation.isValid) {
                          toast.error('Invalid amount', { description: validation.error });
                          return;
                        }
                        
                        setCurrentBatch({ ...currentBatch, revaluationAmount: value });
                      }
                    }}
                    placeholder={getRevaluationAmountPlaceholder(currentBatch.revaluationMode)}
                    readOnly={readonly}
                    tabIndex={4}
                  />
                </div>
              </div>

              {/* Right Column: Notes spanning full height */}
              <div className="flex-1 flex flex-col space-y-2">
                <Label htmlFor="notes">Notes</Label>
                <Textarea
                  id="notes"
                  value={currentBatch.notes}
                  onChange={(e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => setCurrentBatch({ ...currentBatch, notes: e.target.value })}
                  placeholder="Add notes about this revaluation batch..."
                  className="flex-1 resize-none min-h-[100px] bg-white dark:bg-gray-950"
                  readOnly={readonly}
                  tabIndex={5}
                />
              </div>
            </div>
          </div>
        </div>

        {/* Detail Grid */}
        <div 
          className="bg-card" 
          style={{ 
            paddingLeft: 'calc(var(--spacing) * 6)',
            paddingRight: 'calc(var(--spacing) * 6)',
            paddingTop: 'calc(var(--spacing) * 6)',
            paddingBottom: 'calc(var(--spacing) * 6)'
          }}
        >
          <div className="flex items-center justify-between">
            <div>
              <h3 className="text-xl font-semibold">Revaluation Details</h3>
              <p className="text-sm text-muted-foreground mt-1">
                {currentBatch.items.length} {currentBatch.items.length === 1 ? 'asset' : 'assets'} added to this batch
              </p>
            </div>
            {!readonly && (
              <div className="flex items-center gap-3">
                <Button variant="default" className="h-[42px]" onClick={handleAddMoreAssets}>
                  <Plus className="h-4 w-4 mr-2" />
                  Add Assets
                </Button>
                {selectedItemIds.size > 0 && (
                  <DropdownMenu>
                    <DropdownMenuTrigger asChild>
                      <Button variant="outline" size="sm">
                        <MoreVertical className="h-4 w-4 mr-2" />
                        Actions ({selectedItemIds.size})
                      </Button>
                    </DropdownMenuTrigger>
                    <DropdownMenuContent align="end">
                      <DropdownMenuItem onClick={handleRemoveSelectedItems} className="text-destructive">
                        <Trash2 className="h-4 w-4 mr-2" />
                        Remove Selected
                      </DropdownMenuItem>
                    </DropdownMenuContent>
                  </DropdownMenu>
                )}
              </div>
            )}
          </div>
        </div>
        
        <div className="border-y border-border min-h-[calc(100vh-400px)] bg-[#F9FAFB] dark:bg-[#121212]" style={{ 
          paddingLeft: 'calc(var(--spacing) * 6)', 
          paddingRight: 'calc(var(--spacing) * 6)',
          paddingTop: '10px',
          paddingBottom: '10px'
        }}>
          <RevaluationBatchGrid
            items={currentBatch.items}
            onUpdateItem={handleUpdateItem}
            onReapplyDefaults={handleReapplyDefaults}
            selectedItemIds={selectedItemIds}
            onSelectionChange={setSelectedItemIds}
            readonly={readonly}
            revaluationMode={currentBatch.revaluationMode}
            revaluationAmount={currentBatch.revaluationAmount}
          />
        </div>
      </div>

      {/* Sticky Footer - Pinned to bottom */}
      <div className="absolute bottom-0 left-0 right-0 bg-background border-t shadow-lg">
        <div className="px-6 py-4">
          <div className="flex items-center justify-between">
            {/* Left: Action Buttons */}
            <div className="flex gap-2">
              {currentBatch.status === 'In Progress' && (
                <>
                  <Button variant="outline" onClick={handleCancelBatch}>
                    <X className="h-4 w-4 mr-2" />
                    Cancel
                  </Button>
                  <Button variant="outline" onClick={handleSaveBatch}>
                    <Save className="h-4 w-4 mr-2" />
                    Save
                  </Button>
                  <Button onClick={handleProcessBatch}>
                    <CheckCircle2 className="h-4 w-4 mr-2" />
                    Process
                  </Button>
                  <DropdownMenu>
                    <DropdownMenuTrigger asChild>
                      <Button variant="outline" size="icon">
                        <MoreVertical className="h-4 w-4" />
                      </Button>
                    </DropdownMenuTrigger>
                    <DropdownMenuContent align="start" side="top">
                      <DropdownMenuItem>
                        <Download className="h-4 w-4 mr-2" />
                        Export to Excel
                      </DropdownMenuItem>
                    </DropdownMenuContent>
                  </DropdownMenu>
                </>
              )}
              {currentBatch.status === 'Completed' && (
                <>
                  <Button variant="outline" onClick={() => setCurrentBatch(null)}>
                    <X className="h-4 w-4 mr-2" />
                    Back to Dashboard
                  </Button>
                  <DropdownMenu>
                    <DropdownMenuTrigger asChild>
                      <Button variant="outline" size="icon">
                        <MoreVertical className="h-4 w-4" />
                      </Button>
                    </DropdownMenuTrigger>
                    <DropdownMenuContent align="start" side="top">
                      <DropdownMenuItem>
                        <Download className="h-4 w-4 mr-2" />
                        Export to Excel
                      </DropdownMenuItem>
                    </DropdownMenuContent>
                  </DropdownMenu>
                </>
              )}
            </div>

            {/* Center: Spacer */}
            <div className="flex-1" />

            {/* Right: Summary Totals */}
            <div className="flex gap-8">
              <div className="text-right">
                <div className="text-xs text-muted-foreground">Current NBV</div>
                <div className="text-lg font-bold">
                  £{currentBatch.totalCurrentNBV.toLocaleString('en-GB', { minimumFractionDigits: 2 })}
                </div>
              </div>
              <div className="text-right">
                <div className="text-xs text-muted-foreground">Revalued NBV</div>
                <div className="text-lg font-bold">
                  £{currentBatch.totalRevaluedNBV.toLocaleString('en-GB', { minimumFractionDigits: 2 })}
                </div>
              </div>
              <div className="text-right">
                <div className="text-xs text-muted-foreground">Adjustment</div>
                <div className={`text-lg font-bold ${currentBatch.totalRevaluationAdjustment >= 0 ? 'text-green-600' : 'text-red-600'}`}>
                  {currentBatch.totalRevaluationAdjustment >= 0 ? '+' : ''}£{currentBatch.totalRevaluationAdjustment.toLocaleString('en-GB', { minimumFractionDigits: 2 })}
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Modals */}
      <AssetSelectionDialog
        open={showAssetSelection}
        onOpenChange={setShowAssetSelection}
        onConfirm={handleAssetsSelected}
        title="Select Assets for Revaluation"
        description="Choose one or more assets to add to this revaluation batch."
        excludeAssetIds={currentBatch?.items.map(item => item.assetId) || []}
      />

      <ConfirmationModal
        open={showCancelConfirm}
        onOpenChange={setShowCancelConfirm}
        onConfirm={confirmCancelBatch}
        title="Cancel Revaluation Batch"
        description="Are you sure you want to cancel this revaluation batch? All unsaved changes will be lost."
        confirmText="Cancel Batch"
        type="danger"
      />

      <ConfirmationModal
        open={showProcessConfirm}
        onOpenChange={setShowProcessConfirm}
        onConfirm={confirmProcessBatch}
        title="Process Revaluation Batch"
        description="Are you sure you want to process this revaluation batch? This will update the asset values in the system."
        confirmText="Process Batch"
      />

      <ConfirmationModal
        open={showRemoveConfirm}
        onOpenChange={setShowRemoveConfirm}
        onConfirm={confirmRemoveSelectedItems}
        title="Remove Selected Assets"
        description={`Are you sure you want to remove ${selectedItemIds.size} selected asset${selectedItemIds.size !== 1 ? 's' : ''} from this batch?`}
        confirmText="Remove Assets"
        type="danger"
      />

      <DemoModeWarningModal
        open={showDemoWarning}
        onOpenChange={setShowDemoWarning}
        onSwitchToLive={() => setMode('live')}
      />
    </div>
  );
}
